import { useState, useEffect, useMemo, useCallback } from "react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { useTenant } from "../../../../hooks/useTenant";
import { COLLECTIONS } from "../../../../services/collections";
import FinancialCategoryService from "../../../../services/financial_category_service";
import { buildDREData, filterByMonth } from "../utils/dreCalculations";
import { FINANCIAL_STATUS } from "../../../../constants/financial";

/**
 * useDRE
 * Hook que carrega os dados financeiros (financialRecords + categorias)
 * e calcula o DRE para o mês/ano selecionado.
 */
export const useDRE = () => {
    const { idTenant } = useTenant();

    // Filtros
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);

    // Dados
    const [allRecords, setAllRecords] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    /**
     * Carrega todos os financialRecords do ano selecionado e as categorias.
     */
    const loadRecords = useCallback(async () => {
        if (!idTenant) return;

        setLoading(true);
        setError(null);

        try {
            // Carregar categorias
            const cats = await FinancialCategoryService.getAll(idTenant);
            setCategories(cats);

            // Range do ano
            const startDate = `${year}-01-01`;
            const endDate = `${year}-12-31`;

            const q = query(
                collection(db, "tenants", idTenant, COLLECTIONS.FINANCIAL_RECORDS),
                where("competenceDate", ">=", startDate),
                where("competenceDate", "<=", endDate),
                orderBy("competenceDate", "asc")
            );

            const snapshot = await getDocs(q);
            const records = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.status === FINANCIAL_STATUS.CANCELED) return;
                records.push({ id: doc.id, ...data });
            });

            setAllRecords(records);
        } catch (err) {
            console.error("[useDRE] Erro ao carregar dados:", err);
            setError(err.message || "Erro ao carregar dados do DRE.");
        } finally {
            setLoading(false);
        }
    }, [idTenant, year]);

    useEffect(() => {
        loadRecords();
    }, [loadRecords]);

    /**
     * DRE filtrado pelo mês selecionado.
     */
    const dreData = useMemo(() => {
        const emptyTotals = {
            totalIncome: 0, totalIncomeNet: 0, totalIncomeFees: 0,
            totalExpense: 0, totalExpenseNet: 0,
            netResult: 0, grossResult: 0, margin: 0,
        };
        if (!allRecords.length && !categories.length) {
            return { income: [], expense: [], totals: emptyTotals };
        }

        const filtered = filterByMonth(allRecords, year, month);
        return buildDREData(filtered, categories);
    }, [allRecords, categories, year, month]);

    return {
        dreData,
        categories,
        year,
        month,
        setYear,
        setMonth,
        loading,
        error,
        actions: {
            refresh: loadRecords,
        },
    };
};
